import React from 'react';
import RecoverableImage from '../images/RecoverableImage';
import { useMusicGenreCatalogRevision } from '../taste/generated/localization';
import Link from './BrowseLink';
import AlbumContentTitle from '../../components/content/AlbumContentTitle';
import MeasuredContentTitle from '../../components/content/MeasuredContentTitle';
import { useQuery } from '@tanstack/react-query';
import { playlistApi } from '../../api/playlist';
import {
  artistAlbumPath,
  artistAlbumMetadata,
  similarArtistPath,
  playlistArtworkData,
} from '../../pages/artist/artistPresentation';
import PlaylistArtwork from '../../components/content/PlaylistArtwork';
import { BrowseRating } from './BrowseReviewCard';
import { artistSectionTracking } from './artist';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { trackArtistContentClick } from '../../lib/gaEvents';
import { CONTENT_TYPE, ARTIST_ACTION_TYPE, PAGE_TYPE } from '../../constants/gaEnums';
import { useUserStore } from '../../stores/userStore';
export function ArtistImage({ src, className }) {
  return (
    <span className={className}>
      <span aria-hidden="true">postpop</span>
      {src && <RecoverableImage key={src} src={src} alt="" loading="lazy" />}
    </span>
  );
}
function ArtistPlaylistArtwork({ item }) {
  const custom = item.imageUrl?.trim(),
    previews = item.previewImageUrls?.filter(Boolean);
  const key = item.slug || item.id;
  const detail = useQuery({
    queryKey: ['artist-playlist-artwork', key],
    queryFn: ({ signal }) =>
      item.slug
        ? playlistApi.getPlaylistBySlug(item.slug, { signal, suppressGlobalError: true })
        : playlistApi.getPlaylistById(item.id, { signal, suppressGlobalError: true }),
    enabled: Boolean(key) && !custom && !previews?.length,
    staleTime: 300000,
  });
  return <PlaylistArtwork playlist={playlistArtworkData(item, detail.data?.data)} />;
}
export default function ArtistContentCard({
  item,
  kind = 'album',
  section,
  focusKey,
  pageType = PAGE_TYPE.ARTIST,
}) {
  useMusicGenreCatalogRevision();
  const { locale } = useWebLocale(),
    en = locale === 'en';
  const user = useUserStore((s) => s.user);
  const track = () =>
    section &&
    trackArtistContentClick(
      {
        artistSection: artistSectionTracking[section],
        actionType:
          kind === 'artist'
            ? ARTIST_ACTION_TYPE.ARTIST_CLICK
            : kind === 'playlist'
              ? 'playlist_click'
              : ARTIST_ACTION_TYPE.ALBUM_CLICK,
        contentType:
          kind === 'artist'
            ? CONTENT_TYPE.ARTIST
            : kind === 'playlist'
              ? CONTENT_TYPE.LIST
              : CONTENT_TYPE.ALBUM,
        contentId: item.sourceId || item.id,
        contentName: item.title || item.name,
        pageType,
      },
      user,
    );
  if (kind === 'artist')
    return (
      <Link
        data-browse-focus-key={focusKey}
        onClick={track}
        className="pp-artist-person"
        to={similarArtistPath(item)}
      >
        <ArtistImage className="pp-artist-person-art" src={item.imageUrl || item.artistImageUrl} />
        <strong>{item.name}</strong>
      </Link>
    );
  if (kind === 'playlist')
    return (
      <Link
        data-browse-focus-key={focusKey}
        onClick={track}
        className="pp-artist-album pp-artist-playlist"
        to={`/lists/${encodeURIComponent(item.slug || item.id)}`}
      >
        <ArtistPlaylistArtwork item={item} />
        <strong>{item.title}</strong>
        <span className="pp-artist-card-meta">
          {item.username ?? 'postpop'} ·{' '}
          {item.type === 'TRACK'
            ? en
              ? `${item.trackCount || 0} tracks`
              : `${item.trackCount || 0}곡`
            : en
              ? `${item.albumCount || 0} albums`
              : `앨범 ${item.albumCount || 0}장`}
        </span>
      </Link>
    );
  const metadata = artistAlbumMetadata(item, locale);
  return (
    <Link
      data-browse-focus-key={focusKey}
      onClick={track}
      className="pp-artist-album"
      to={artistAlbumPath(item)}
    >
      <ArtistImage className="pp-artwork" src={item.albumImageUrl || item.imageUrl} />
      <div className="pp-artist-album-copy">
        <AlbumContentTitle item={item} title={item.title} titleComponent={MeasuredContentTitle} />
        {metadata && <span className="pp-artist-card-meta">{metadata}</span>}
        <BrowseRating value={item.averageRating} accent />
      </div>
    </Link>
  );
}
